import { api } from './api';
import { ActionDispatching, CaseData, ConstraintResponseType } from 'app/types-shared';
import { ReceiveCallsToActions, ReceiveChangeCaseStatus, RequestCallsToActions, 
  RequestChangeCaseStatus} from 'app/area/case/features/calls-to-actions/action-types';
import { toastFactory } from 'app/infrastructure/notifications/toast-factory';
import { ComplexValue } from 'ui';
import { getUserMessage } from 'app/infrastructure/notifications/user-messages/get-user-message';
import { ReceiveCase, ReceiveChangeConnectionSequence } from 'app/area/case/features/connected-cases/action-types';
import { actionCreators as responseCachingActionCreators } from 'app/area/shared/response-caching/actions';
import { caseTypeId as getCaseTypeId, caseContextId as getCaseContextId } from 'app/infrastructure/data/wireformat';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { ConnectedCaseActionTypes } from 'app/components/connect-existing-cases/connect-existing-cases';
import { connectedCasesIdsByConnectionType } from 'app/area/case/features/calls-to-actions/mappings';
import { ReceiveConnectedCasesByConnectionType } from 'app/area/bowtie/action-types';


const toasts = toastFactory();
export const actionCreators = {     
  callsToActions: (caseId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      await dispatch(new RequestCallsToActions(caseId));
      try {
        const callsToActions = await api.callsToActions(caseId);
        await dispatch(new ReceiveCallsToActions(callsToActions));
        return callsToActions;
      }
      catch(err) {
        toasts.dispatchError(getUserMessage(err.userMessages));
      }
    },

    onChangeCaseStatus: (caseId: number, status : ComplexValue<number>, allowWarnings: boolean = true): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const caseResponse = await api.case(caseId);
        const etag = caseResponse.etag;
        const caseData = caseResponse.json.data;
        const statusField = caseData.fields.Status?.values?.[0];
        if(!statusField) {
          return null;
        }
        statusField.v = status?.value;
        statusField.d = status?.description;
        await dispatch(new RequestChangeCaseStatus(caseId));
        const saveCaseResponse = await api.changeCaseStatus(caseData, etag, true, allowWarnings);   
        await dispatch(new ReceiveChangeCaseStatus(status));
        if(allowWarnings) {
          const warnings = saveCaseResponse.json.data.metaData.failedDomainConstraints;
          if(!!warnings?.length) {
            return { warnings: warnings };
          }
        }
      }
      catch(err) {
        return { userMessages: err.userMessages, 
          validationErrors: err.validationErrors?.filter(err=>err.responseType === ConstraintResponseType.Error),
          warnings: err.validationErrors?.filter(err=>err.responseType === ConstraintResponseType.Warning)}
    }
    return null;
  },

  onChangeCaseConnection: (
    caseId: number,
    connectedCaseId: number,
    connectionTypeId: number,
    currentConnectedCases: number[],
    type: ConnectedCaseActionTypes,
    allowWarnings: boolean = true,
    bowtie?: boolean,
    instanceKey?: string
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const utcOffset = getState().app.settings.utcOffset;
        const caseResponse = await api.case(caseId);
        const etag = caseResponse.etag;

        const result = await api.onChangeCaseConnection({
          caseId,
          connectedCaseId,
          connectionTypeId,
          currentConnectedCases,
          type,
          etag,
          allowWarnings,
          utcOffset
        });

        if (!bowtie) {
          return updateCaseReadConnectedCases(result, dispatch, caseId, connectedCaseId);
        } else {
          return updateCaseReportConnectedCases(result, dispatch, caseId, instanceKey, connectedCaseId);
        }
      } catch (err) {
        return handleErrors(err, dispatch, allowWarnings);
      }
    }
};

const updateCaseReadConnectedCases = async (
  caseData: CaseData,
  dispatch: any,
  caseId: number,
  connectedCaseId: number
) => {
  try {
    if (!caseData) {
      return null;
    }
    const caseType = getCaseTypeId(caseData);
    const caseContextId = getCaseContextId(caseData);
    const uiDef = await dispatch(responseCachingActionCreators.getUiDefinition(caseType, caseContextId));
    if (!uiDef) {
      return null;
    }
    dispatch(new ReceiveCase(caseId, caseData, { ...uiDef, applicationId: caseType }));
    dispatch(new ReceiveChangeConnectionSequence(connectedCaseId));
  } catch (err) {
    dispatch(new ErrorNotification(err.userMessages));
  }
  return null;
};

const updateCaseReportConnectedCases = async (
  caseData: CaseData,
  dispatch: any,
  caseId: number,
  instanceKey?: string,
  connectedCaseId?: number
) => {
  try {
    const groupedByConnectionType = connectedCasesIdsByConnectionType(caseData);
    dispatch(new ReceiveConnectedCasesByConnectionType(caseId, groupedByConnectionType, instanceKey));
    dispatch(new ReceiveChangeConnectionSequence(connectedCaseId));
  } catch (err) {
    dispatch(new ErrorNotification(err.userMessages));
  }
  return null;
};

const handleErrors = (err: any, dispatch: any, allowWarnings: boolean) => {
  const errors = err?.validationErrors?.filter(error => error.responseType === ConstraintResponseType.Error);
  const warnings = err?.validationErrors?.filter(error => error.responseType === ConstraintResponseType.Warning);
  if (errors?.length) {
    const messages = errors.map(e => e.message).join('\n');
    toasts.dispatchError(messages);
    return { errors: true };
  } else if (warnings?.length && !allowWarnings) {
    return { warnings };
  } else if (err?.userMessages) {
    dispatch(new ErrorNotification(err.userMessages));
  }
  return null;
};

